import { db } from '@/utils/firebase.js';
import axiosIntents from '@/utils/axios-intents';
import { sanitizeErrorMessage } from '@/helpers/sanitize-text';

/**
 * POI (Proof of Identity) Verification Module
 *
 * This module manages the POI verification flow for product purchases.
 * It handles verification status, form data, and integration with backend POI provider (Centrix).
 */

const COLLECTION_VERIFICATIONS = 'verifications';


let unsubscribeVerificationListener = null;


const VERIFICATION_TIMEOUT_MS = 2 * 60 * 1000;
let verificationTimeoutId = null;

export default {
    state: {

        current_sub_step: 1,

        selected_document_type: null,

        available_document_types_count: 0,
        
        driver_license_form: {
            number: '',
            version: '',
            firstName: '',
            lastName: '',
            middleName: '',
            gender: '',
            dateOfBirth: {
                day: '',
                month: '',
                year: '',
            },
        },
        
        passport_form: {
            number: '',
            firstName: '',
            lastName: '',
            middleName: '',
            gender: '',
            dateOfBirth: {
                day: '',
                month: '',
                year: '',
            },
            expiryDate: {
                day: '',
                month: '',
                year: '',
            },
        },
        
        address_form: {
            streetNumber: '',
            addressLine1: '',
            suburb: '',
            city: '',
            postcode: '',
            yearsAtAddress: '',
        },
        
        verification_status: {
            verified: false,
            status: '',
            verificationID: null,
            provider: '',
            verificationType: '',
            verifiedAtNanos: null,
            error: null,
            verificationData: null, 
        },
        

        verification_loading: false,

        verification_loading_modal_visible: false,
    },

    getters: {

        poi_current_sub_step: state => state.current_sub_step,


        poi_selected_document_type: state => state.selected_document_type,

        poi_driver_license_form: state => state.driver_license_form,
        poi_passport_form: state => state.passport_form,
        poi_address_form: state => state.address_form,


        poi_verification_status: state => state.verification_status,
        poi_verification_loading: state => state.verification_loading,

        poi_verification_loading_modal_visible: state => state.verification_loading_modal_visible,
        
        /**
         * Check if customer is already verified
         * @returns {boolean} - true if customer is verified, false otherwise
         *
         * Checks both:
         * 1. Cart POI.CustomerVerificationStatus === "verified"
         * 2. State verification_status.verified === true
         */
        isCustomerVerified: (state, getters) => {

            const poiData = getters.poi_cart_data;
            if (poiData?.CustomerVerificationStatus === 'verified') {
                return true;
            }

            return state.verification_status.verified === true;
        },
        
        /**
         * Determine if Verify step should be shown in checkout
         * @param {object} state - Vuex state
         * @param {object} getters - Vuex getters
         * @returns {boolean} - true if Verify step should be shown
         *
         * Show Verify step if:
         * 1. POI.Required === true in cart response
         *
         * NOTE: We show the step even if customer is already verified or verification failed,
         * because the Verify component will display ONLY the result screen (step 5).
         * The component itself handles the logic of showing result vs. forms.
         */
        shouldShowVerifyStep: (_state, _getters, _rootState, rootGetters) => {
            const cart = rootGetters.eshop_v2_cart;
            const poiRequired = cart?.POI?.Required === true;

            return poiRequired;
        },


        poi_cart_data: (_state, _getters, _rootState, rootGetters) => {
            return rootGetters.eshop_v2_cart?.POI || null;
        },
        
        poi_allowed_document_types: (_state, getters) => {
            const poiData = getters.poi_cart_data;
            if (!poiData || !poiData.AllowedDocTypes) {
                return [];
            }

            if (Array.isArray(poiData.AllowedDocTypes)) {

                return poiData.AllowedDocTypes.map(type => {
                    const upperType = String(type).toUpperCase();
                    if (upperType === 'DL') return 'driver_licence';
                    if (upperType === 'PASSPORT') return 'passport';
                    return type.toLowerCase().trim();
                });
            } else {

                return poiData.AllowedDocTypes.split(',').map(type => type.trim());
            }
        },

        poi_available_document_types_count: (_state, getters) => {
            const allowedTypes = getters.poi_allowed_document_types;
            return allowedTypes.length;
        },
    },
    
    mutations: {

        SET_POI_CURRENT_SUB_STEP(state, step) {
            state.current_sub_step = step;
        },

        SET_POI_SELECTED_DOCUMENT_TYPE(state, documentType) {
            state.selected_document_type = documentType;
        },

        SET_AVAILABLE_DOCUMENT_TYPES_COUNT(state, count) {
            state.available_document_types_count = count;
        },

        UPDATE_POI_DRIVER_LICENSE_FORM(state, formData) {
            state.driver_license_form = { ...state.driver_license_form, ...formData };
        },

        UPDATE_POI_PASSPORT_FORM(state, formData) {
            state.passport_form = { ...state.passport_form, ...formData };
        },

        UPDATE_POI_ADDRESS_FORM(state, formData) {
            state.address_form = { ...state.address_form, ...formData };
        },

        SET_POI_VERIFICATION_STATUS(state, status) {
            state.verification_status = { ...state.verification_status, ...status };
        },

        SET_POI_VERIFICATION_LOADING(state, loading) {
            state.verification_loading = loading;
        },

        SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE(state, visible) {
            state.verification_loading_modal_visible = visible;
        },

        RESET_POI_DATA(state) {
            state.current_sub_step = 1;
            state.selected_document_type = null;
            state.available_document_types_count = 0;
            state.driver_license_form = {
                number: '',
                version: '',
                firstName: '',
                lastName: '',
                middleName: '',
                gender: '',
                dateOfBirth: {
                    day: '',
                    month: '',
                    year: '',
                },
            };
            state.passport_form = {
                number: '',
                firstName: '',
                lastName: '',
                middleName: '',
                gender: '',
                dateOfBirth: {
                    day: '',
                    month: '',
                    year: '',
                },
                expiryDate: {
                    day: '',
                    month: '',
                    year: '',
                },
            };
            state.address_form = {
                streetNumber: '',
                addressLine1: '',
                suburb: '',
                city: '',
                postcode: '',
                yearsAtAddress: '',
            };
            state.verification_status = {
                verified: false,
                status: '',
                verificationID: null,
                provider: '',
                verificationType: '',
                verifiedAtNanos: null,
                error: null,
                verificationData: null,
            };
        },

        FIREBASE_LOGOUT(state) {
            state.current_sub_step = 1;
            state.selected_document_type = null;
            state.available_document_types_count = 0;

            state.driver_license_form = {
                number: '',
                version: '',
                firstName: '',
                lastName: '',
                middleName: '',
                gender: '',
                dateOfBirth: {
                    day: '',
                    month: '',
                    year: '',
                },
            };
            state.passport_form = {
                number: '',
                firstName: '',
                lastName: '',
                middleName: '',
                gender: '',
                dateOfBirth: {
                    day: '',
                    month: '',
                    year: '',
                },
                expiryDate: {
                    day: '',
                    month: '',
                    year: '',
                },
            };
            state.address_form = {
                streetNumber: '',
                addressLine1: '',
                suburb: '',
                city: '',
                postcode: '',
                yearsAtAddress: '',
            };
            state.verification_status = {
                verified: false,
                status: '',
                verificationID: null,
                provider: '',
                verificationType: '',
                verifiedAtNanos: null,
                error: null,
                verificationData: null,
            };
        },
    },

    actions: {
        /**
         * Initialize POI verification status from cart data
         * Called when cart is loaded to check if customer is already verified
         *
         * CRITICAL: This action also clears form data when there's no POI data in cart
         * to prevent data leakage between different customers/orders
         */
        initializePOIFromCart({ commit, getters, state }) {
            const poiData = getters.poi_cart_data;

            if (!poiData) {
                commit('RESET_POI_DATA');
                return;
            }

            if (poiData.CustomerVerificationStatus === 'verified') {
                commit('SET_POI_VERIFICATION_STATUS', {
                    verified: true,
                    status: 'verified',
                    verificationID: null, 
                    provider: poiData.Provider || '',
                    verificationType: '', 
                    verifiedAtNanos: null, 
                    error: null,
                    verificationData: null,
                });
            } else if (poiData.CustomerVerificationStatus === 'failed') {

                commit('SET_POI_VERIFICATION_STATUS', {
                    verified: false,
                    status: 'failed',
                    verificationID: null,
                    provider: poiData.Provider || '',
                    verificationType: '', 
                    verifiedAtNanos: null,
                    error: 'Verification failed. Please try again or contact support.',
                    verificationData: null,
                });
            } else {
                if (state.verification_status.status === 'pending') {
                    return;
                }

                if (state.verification_status.status === 'failed') {
                    return;
                }

                commit('RESET_POI_DATA');
            }
        },

        /**
         * Navigate to a specific sub-step
         */
        setPoiSubStep({ commit }, step) {
            commit('SET_POI_CURRENT_SUB_STEP', step);
        },

        /**
         * Navigate to next sub-step
         */
        nextPoiSubStep({ state, commit }) {
            if (state.current_sub_step < 4) {
                commit('SET_POI_CURRENT_SUB_STEP', state.current_sub_step + 1);
            }
        },

        /**
         * Navigate to previous sub-step
         */
        previousPoiSubStep({ state, commit }) {
            if (state.current_sub_step > 1) {
                commit('SET_POI_CURRENT_SUB_STEP', state.current_sub_step - 1);
            }
        },

        /**
         * Select document type and auto-navigate to sub-step 2
         */
        selectDocumentType({ commit }, documentType) {
            commit('SET_POI_SELECTED_DOCUMENT_TYPE', documentType);

            commit('SET_POI_CURRENT_SUB_STEP', 2);
        },

        /**
         * Set the number of available document types
         * Used for Back button navigation logic
         */
        setAvailableDocumentTypesCount({ commit }, count) {
            commit('SET_AVAILABLE_DOCUMENT_TYPES_COUNT', count);
        },

        /**
         * Update form data
         */
        updateDriverLicenseForm({ commit }, formData) {
            commit('UPDATE_POI_DRIVER_LICENSE_FORM', formData);
        },

        updatePassportForm({ commit }, formData) {
            commit('UPDATE_POI_PASSPORT_FORM', formData);
        },

        updateAddressForm({ commit }, formData) {
            commit('UPDATE_POI_ADDRESS_FORM', formData);
        },

        /**
         * Show loading modal
         */
        showPoiLoadingModal({ commit }) {
            commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', true);
        },

        /**
         * Hide loading modal
         */
        hidePoiLoadingModal({ commit }) {
            commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', false);
        },

        /**
         * Reset all POI verification data
         * Used when verification fails or user navigates back from failed state
         */
        resetPoiData({ commit }) {
            commit('RESET_POI_DATA');
        },

        /**
         * Submit POI verification to backend
         *
         * @param {object} context - Vuex context
         * @returns {Promise} - Resolves when verification is submitted (202 Accepted)
         *
         * NOTE: Backend returns 202 Accepted immediately.
         * Actual verification result will be updated in Firestore asynchronously.
         * Component should listen to Firestore changes for verification result.
         */
        async submitPOIVerification({ state, commit, getters, rootGetters }) {
            commit('SET_POI_VERIFICATION_LOADING', true);

            commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', true);

            try {
                const customerUUID = rootGetters.current_account_uid;
                const poiData = getters.poi_cart_data;

                if (!customerUUID) {
                    throw new Error('Customer UUID not found');
                }

                if (!poiData || !poiData.Provider) {
                    throw new Error('POI provider not configured');
                }

                const payload = buildVerificationPayload(state, poiData.Provider);

                const response = await axiosIntents.post(
                    `/poi/v1/customer/${customerUUID}/verification`,
                    payload
                );

                const verificationID = response.data?.verificationID || response.data?.VerificationID;

                if (!verificationID) {
                    throw new Error('Verification ID not received from backend');
                }

                commit('SET_POI_VERIFICATION_STATUS', {
                    status: 'pending',
                    verified: false,
                    verificationID: verificationID,
                    provider: poiData.Provider,
                    verificationType: state.selected_document_type,
                });
                if (verificationTimeoutId) {
                    clearTimeout(verificationTimeoutId);
                }
                verificationTimeoutId = setTimeout(() => {

                    if (state.verification_status.status === 'pending') {
                        commit('SET_POI_VERIFICATION_STATUS', {
                            status: 'timeout',
                            verified: false,
                            error: 'Sorry, we could not verify your identity. Please try again.',
                        });
                        commit('SET_POI_VERIFICATION_LOADING', false);

                        commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', false);

                        verificationTimeoutId = null;
                    }
                }, VERIFICATION_TIMEOUT_MS);

                return Promise.resolve({ verificationID });
            } catch (error) {
                commit('SET_POI_VERIFICATION_LOADING', false);

                commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', false);


                if (error.response) {
                    const { status, data } = error.response;

                    // Extract and sanitize backend error message
                    const backendMessage = data.message || data.error?.message || data.err_message;
                    const sanitizedMessage = backendMessage ? sanitizeErrorMessage(backendMessage) : null;

                    if (status === 400) {
                        const errorMessage = sanitizedMessage || 'Invalid verification data';
                        throw new Error(errorMessage);
                    } else if (status === 422) {
                        const errorMessage = sanitizedMessage || 'Verification failed';
                        commit('SET_POI_VERIFICATION_STATUS', {
                            status: 'failed',
                            verified: false,
                            error: errorMessage,
                        });
                        throw new Error(errorMessage);
                    }
                }

                throw error;
            }
        },

        subscribeToVerificationStatus({ commit, state, rootGetters }, verificationID) {
            const customerUUID = rootGetters.current_account_uid;

            if (!customerUUID) {
                return Promise.reject('Customer UUID not found');
            }


            const targetVerificationID = verificationID || state.verification_status.verificationID;

            if (!targetVerificationID) {
                return Promise.resolve();
            }


            if (unsubscribeVerificationListener) {
                unsubscribeVerificationListener();
                unsubscribeVerificationListener = null;
            }


            const verificationRef = db
                .collection('customers')
                .doc(customerUUID)
                .collection(COLLECTION_VERIFICATIONS)
                .doc(targetVerificationID);

            unsubscribeVerificationListener = verificationRef.onSnapshot(
                (doc) => {
                    if (doc.exists) {
                        const data = doc.data();

                        let mappedStatus = 'pending';
                        let verified = false;
                        let errorMessage = null;

                        if (data.Status === 'completed') {
                            if (data.Verified === true) {
                                mappedStatus = 'verified';
                                verified = true;
                            } else {
                                mappedStatus = 'failed';
                                verified = false;

                                // Extract and sanitize error message from Firestore
                                const backendError = data.ErrorMessage || data.Error || data.Result?.error_message || data.Result?.ErrorMessage;
                                if (backendError) {
                                    errorMessage = sanitizeErrorMessage(backendError);
                                }
                            }
                        } else if (data.Status === 'in_progress') {
                            mappedStatus = 'pending';
                            verified = false;
                        }

                        commit('SET_POI_VERIFICATION_STATUS', {
                            verified: verified,
                            status: mappedStatus,
                            verificationID: data.VerificationId || targetVerificationID,
                            provider: data.Provider || '',
                            verificationType: data.VerificationType || '',
                            verifiedAtNanos: data.Result?.verified_at_nanos || data.CompletedAtNanos || null,
                            error: errorMessage,

                            verificationData: data,
                        });


                        if (mappedStatus === 'verified' || mappedStatus === 'failed') {
                            commit('SET_POI_VERIFICATION_LOADING', false);

                            commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', false);


                            if (verificationTimeoutId) {
                                clearTimeout(verificationTimeoutId);
                                verificationTimeoutId = null;
                            }
                        }
                    }
                },
                (error) => {
                    console.error('Error listening to verification status:', error);
                    commit('SET_POI_VERIFICATION_LOADING', false);

                    commit('SET_POI_VERIFICATION_LOADING_MODAL_VISIBLE', false);
                }
            );

            return Promise.resolve();
        },

        /**
         * Unsubscribe from Firestore verification status updates
         */
        unsubscribeFromVerificationStatus() {
            if (unsubscribeVerificationListener) {
                unsubscribeVerificationListener();
                unsubscribeVerificationListener = null;
            }
        },
    },
};

/**
 * Build verification payload for API request
 *
 * @param {object} state - POI module state
 * @param {string} provider - POI provider (e.g., 'centrix')
 * @returns {object} - Verification payload
 */
function buildVerificationPayload(state, provider) {
    const { selected_document_type, driver_license_form, passport_form, address_form } = state;


    const payload = {
        Provider: provider,
        VerificationType: selected_document_type,
        Metadata: {
            ConsentGiven: true,
            Timestamp: Date.now() * 1000000, // Convert to nanoseconds (milliseconds * 1000000)
            YearsAtAddress: parseInt(address_form.yearsAtAddress) || 0,
        },
        Address: {
            AddressLine1: address_form.addressLine1,
            AddressLine2: address_form.streetNumber,
            Suburb: address_form.suburb,
            City: address_form.city,
            Postcode: address_form.postcode,
            Country: 'NZL',
        },
    };

    if (selected_document_type === 'driver_licence') {
        payload.DriverLicence = {
            Number: driver_license_form.number,
            Version: driver_license_form.version,
            FirstName: driver_license_form.firstName,
            LastName: driver_license_form.lastName,
            MiddleName: driver_license_form.middleName,
            Gender: driver_license_form.gender,
            DateOfBirth: formatDateOfBirth(driver_license_form.dateOfBirth),
        };
    } else if (selected_document_type === 'passport') {
        payload.Passport = {
            Number: passport_form.number,
            FirstName: passport_form.firstName,
            LastName: passport_form.lastName,
            MiddleName: passport_form.middleName,
            Gender: passport_form.gender,
            DateOfBirth: formatDateOfBirth(passport_form.dateOfBirth),
            ExpiryDate: formatDateOfBirth(passport_form.expiryDate),
        };
    }

    return payload;
}

/**
 * Format date of birth to YYYY-MM-DD
 *
 * @param {object} dob - Date of birth object with day, month, year
 * @returns {string} - Formatted date string
 */
function formatDateOfBirth(dob) {
    const { year, month, day } = dob;
    const paddedMonth = String(month).padStart(2, '0');
    const paddedDay = String(day).padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
}

